import React from "react";
import {
  Content,
  Header,
  Item,
  Root,
  Trigger,
} from "@radix-ui/react-accordion";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cn } from "../../../utils/cn";
import { useTranslation } from "react-i18next";

// --- Sub-Components ---

// Accordion Item Wrapper
const AccordionItem = React.forwardRef<
  React.ElementRef<typeof Item>,
  React.ComponentPropsWithoutRef<typeof Item>
>(({ className, ...props }, ref) => (
  <Item
    ref={ref}
    className={cn("border-b border-white/10", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

// Accordion Trigger (Header + Button)
const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof Trigger>,
  React.ComponentPropsWithoutRef<typeof Trigger>
>(({ className, children, ...props }, ref) => (
  <Header className="flex">
    <Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-6 font-medium transition-all hover:text-green-400 [&[data-state=open]>svg]:rotate-180",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 text-gray-400" />
    </Trigger>
  </Header>
));
AccordionTrigger.displayName = "AccordionTrigger";

// Accordion Content (The hidden answer)
const AccordionContent = React.forwardRef<
  React.ElementRef<typeof Content>,
  React.ComponentPropsWithoutRef<typeof Content>
>(({ className, children, ...props }, ref) => (
  <Content
    ref={ref}
    className={cn(
      "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      className,
    )}
    {...props}
  >
    <div
      className={cn(
        "pb-6 pt-0 text-gray-400 leading-relaxed whitespace-pre-line select-none",
        className,
      )}
    >
      {children}
    </div>
  </Content>
));
AccordionContent.displayName = "AccordionContent";

// --- Main Section Component ---

const FAQSection: React.FC = () => {
  const { t } = useTranslation();
  const faqData = t("landingPage.faqs.items", {
    returnObjects: true,
  }) as { question: string; answer: string }[];

  return (
    <section id="faqs" className="py-20 px-4 bg-transparent text-white">
      <div className="container mx-auto max-w-3xl">
        {/* Section Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          // @ts-expect-error - Tailwind CSS classes
          className="text-3xl md:text-4xl font-bold text-center mb-12"
        >
          {t("landingPage.faqs.title")}
        </motion.h2>

        {/* Accordion List */}
        <Root type="single" collapsible className="space-y-4">
          {faqData?.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05, duration: 0.5 }}
            >
              <AccordionItem
                value={`item-${index}`}
                // "card-glass" replacement:
                className="bg-white/5 backdrop-blur-sm px-6 border border-white/10 rounded-xl"
              >
                <AccordionTrigger className="text-left text-lg text-white hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Root>
      </div>
    </section>
  );
};

export { FAQSection as FAQs };
